import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/api';
import CitySelector from '@/components/CitySelector';
import { 
  ArrowRight, Building2, Palette, Key, CheckCircle, Play, Pause,
  Search, Home as HomeIcon, Users, Briefcase, MapPin, Star,
  Clock, Shield, Sparkles, ChevronRight, Phone
} from 'lucide-react';

// Hero slides data
const heroSlides = [
  {
    id: 'real-estate',
    title: 'Find Your Dream Property',
    subtitle: 'Residential • Commercial • Agricultural',
    description: 'Discover premium properties with verified listings, advanced search, and expert guidance.',
    cta: 'Explore Properties',
    ctaLink: '/real-estate',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=2000&q=80',
    icon: Building2,
    color: '#15803D'
  },
  {
    id: 'interior',
    title: 'Transform Your Space',
    subtitle: 'AI-Powered Design • Z-Engine Quotes',
    description: 'Get instant cost estimates and work with expert designers to create your dream interiors.',
    cta: 'Get Design Quote',
    ctaLink: '/interior-design',
    image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=2000&q=80',
    icon: Palette,
    color: '#D97706'
  },
  {
    id: 'management',
    title: 'Hassle-Free Property Management',
    subtitle: 'Rentals • Maintenance • Peace of Mind',
    description: 'Let us handle your property while you focus on what matters. Full-service management solutions.',
    cta: 'Learn More',
    ctaLink: '/property-management',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=2000&q=80',
    icon: Key,
    color: '#7C3AED'
  },
  {
    id: 'rent-lease',
    title: 'Rent or Lease Properties',
    subtitle: 'Find Tenants • List Rentals • Flexible Terms',
    description: 'Connect with verified tenants or find your ideal rental property with our comprehensive listings.',
    cta: 'Browse Rentals',
    ctaLink: '/real-estate?type=rental',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=2000&q=80',
    icon: HomeIcon,
    color: '#0891B2'
  }
];

// Process steps for Real Estate
const realEstateProcess = [
  { step: 1, title: 'Search', description: 'Browse verified listings with smart filters', icon: Search },
  { step: 2, title: 'Shortlist', description: 'Save favorites and compare properties', icon: Star },
  { step: 3, title: 'Visit', description: 'Schedule site visits at your convenience', icon: MapPin },
  { step: 4, title: 'Finalize', description: 'Complete documentation with our support', icon: Shield },
];

const interiorProcess = [
  { step: 1, title: 'Get Quote', description: 'Instant AI-powered cost estimate', icon: Sparkles },
  { step: 2, title: 'Consultation', description: 'Discuss your vision with experts', icon: Users },
  { step: 3, title: '3D Design', description: 'Visualize before execution', icon: Palette },
  { step: 4, title: 'Execution', description: 'Quality delivery on time', icon: CheckCircle },
];

const managementProcess = [
  { step: 1, title: 'List', description: 'Register your property with us', icon: HomeIcon },
  { step: 2, title: 'Market', description: 'We find the right tenants', icon: Users },
  { step: 3, title: 'Manage', description: 'Complete property care', icon: Key },
  { step: 4, title: 'Earn', description: 'Hassle-free rental income', icon: Star },
];

// Process Trail Component
const ProcessTrail = ({ steps, activeStep = 0 }) => {
  // Clamp the progress to not exceed 100% and prevent overflow
  const progressPercent = Math.min((activeStep / (steps.length - 1)) * 100, 100);
  
  return (
    <div className="relative" data-testid="process-trail">
      {/* Trail Line - overflow hidden prevents bar from protruding */}
      <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-primary to-amber-500 rounded-full transition-all duration-500"
          style={{ width: `${progressPercent}%`, maxWidth: '100%' }}
        />
      </div>
      
      {/* Steps */}
      <div className="relative flex justify-between">
        {steps.map((item, idx) => (
          <div 
            key={idx} 
            className={`flex flex-col items-center text-center transition-all duration-300 ${idx <= activeStep ? 'opacity-100' : 'opacity-50'}`}
          >
            <div 
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                idx < activeStep 
                  ? 'bg-primary text-white' 
                  : idx === activeStep 
                    ? 'bg-amber-500 text-white ring-4 ring-amber-200 scale-110' 
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {idx < activeStep ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <item.icon className="w-5 h-5" />
              )}
            </div>
            <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
            <p className="text-xs text-gray-500 max-w-[100px] hidden md:block">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// User Type Card
const UserTypeCard = ({ icon: Icon, title, description, features, ctaText, ctaLink, color }) => {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
      <div 
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-gray-600 text-sm mb-4">{description}</p>
      <ul className="space-y-2 mb-6">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-center text-sm text-gray-700">
            <CheckCircle className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
      <Button 
        onClick={() => navigate(ctaLink)}
        className="w-full rounded-full group-hover:bg-primary group-hover:text-white transition-colors"
        variant="outline"
      >
        {ctaText} <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [activeRealEstateStep, setActiveRealEstateStep] = useState(0);
  const [activeInteriorStep, setActiveInteriorStep] = useState(0);
  const [activeManagementStep, setActiveManagementStep] = useState(0);
  const [selectedCity, setSelectedCity] = useState('Hyderabad');
  const [quickStats, setQuickStats] = useState({ properties_listed: 0, happy_clients: 0 });

  // Pause carousel on any interaction (only resume via play button)
  const pauseCarousel = useCallback(() => {
    setIsPaused(true);
  }, []);

  // Fetch quick stats from backend
  useEffect(() => {
    const fetchQuickStats = async () => {
      try {
        const response = await apiClient.get('/stats/quick-stats');
        setQuickStats(response.data);
      } catch (error) {
        // Fallback to default values if API fails
        setQuickStats({ properties_listed: 500, happy_clients: 200 });
      }
    };
    fetchQuickStats();
  }, []);

  // Auto-rotate carousel (only when not paused)
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused]);

  // Animate process steps on scroll (simplified)
  useEffect(() => {
    const animateSteps = () => {
      setActiveRealEstateStep(prev => (prev + 1) % 5);
      setActiveInteriorStep(prev => (prev + 1) % 5);
      setActiveManagementStep(prev => (prev + 1) % 5);
    };
    const interval = setInterval(animateSteps, 2000);
    return () => clearInterval(interval);
  }, []);

  const currentHero = heroSlides[currentSlide];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Carousel */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Images */}
        {heroSlides.map((slide, idx) => (
          <div 
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30 md:from-black/80 md:via-black/50 md:to-transparent" />
          </div>
        ))}
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-24 pb-8">
          <div className="max-w-2xl">
            {/* Slide Indicator Pills - Scrollable on mobile */}
            <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-2 px-2">
              {heroSlides.map((slide, idx) => (
                <button
                  key={slide.id}
                  onClick={() => {
                    setCurrentSlide(idx);
                    pauseCarousel(); // Pause carousel when user selects a slide
                  }}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-all duration-300 flex-shrink-0 ${
                    idx === currentSlide 
                      ? 'bg-white text-gray-900' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  data-testid={`slide-indicator-${slide.id}`}
                >
                  <slide.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm font-medium">
                    {slide.id === 'real-estate' ? 'Property' : 
                     slide.id === 'interior' ? 'Interior' : 
                     slide.id === 'management' ? 'Manage' : 'Rent'}
                  </span>
                </button>
              ))}
              
              {/* Play/Pause Button - Only way to resume auto-scroll */}
              <button
                onClick={() => setIsPaused(!isPaused)}
                className={`ml-auto p-1.5 sm:p-2 rounded-full transition-colors flex-shrink-0 ${
                  isPaused 
                    ? 'bg-primary text-white hover:bg-primary/90' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid="carousel-play-pause-btn"
                title={isPaused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Main Content */}
            <div className="animate-in fade-in slide-in-from-left duration-500" key={currentSlide}>
              <p className="text-primary-foreground/80 text-xs sm:text-sm font-medium tracking-wider uppercase mb-2 sm:mb-3">
                {currentHero.subtitle}
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-testid="hero-title">
                {currentHero.title}
              </h1>
              <p className="text-base sm:text-lg text-white/80 mb-6 sm:mb-8 max-w-xl">
                {currentHero.description}
              </p>
              <div className="flex gap-3 sm:gap-4 flex-wrap">
                <Button 
                  size="lg" 
                  onClick={() => {
                    pauseCarousel();
                    const cityParam = selectedCity ? `?city=${encodeURIComponent(selectedCity)}` : '';
                    navigate(`${currentHero.ctaLink}${cityParam}`);
                  }}
                  className="rounded-full bg-white text-gray-900 hover:bg-white/90 px-5 sm:px-8 py-4 sm:py-6 text-sm sm:text-base shadow-xl"
                  data-testid="hero-cta-btn"
                >
                  {currentHero.cta} <ArrowRight className="ml-1.5 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => {
                    pauseCarousel();
                    navigate('/register');
                  }}
                  className="rounded-full border-white/50 text-white hover:bg-white/10 px-5 sm:px-8 py-4 sm:py-6 text-sm sm:text-base"
                >
                  Get Started
                </Button>
              </div>
              
              {/* City Selector - Center, below CTAs */}
              <div className="mt-6 sm:mt-8">
                <p className="text-white/60 text-sm mb-2">Select your city</p>
                <CitySelector 
                  selectedCity={selectedCity}
                  onCityChange={setSelectedCity}
                  variant="hero"
                  onInteractionStart={pauseCarousel}
                  onInteractionEnd={() => {}} // No auto-resume, only play button resumes
                />
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-8 sm:mt-12 flex gap-2">
              {heroSlides.map((_, idx) => (
                <div 
                  key={idx} 
                  className="h-1 rounded-full bg-white/20 flex-1 overflow-hidden"
                >
                  <div 
                    className={`h-full bg-white transition-all duration-300 ${
                      idx === currentSlide ? 'animate-progress' : idx < currentSlide ? 'w-full' : 'w-0'
                    }`}
                    style={idx === currentSlide && !isPaused ? { animation: 'progress 5s linear' } : {}}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Glassmorphism Info Card */}
        <div className="absolute bottom-8 right-8 hidden lg:block">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 max-w-xs" data-testid="quick-stats-card">
            <div className="flex items-center gap-3 mb-3">
              <currentHero.icon className="w-8 h-8 text-white" />
              <span className="text-white font-semibold">Quick Stats</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-white/90 text-sm">
              <div>
                <p className="text-2xl font-bold" data-testid="stats-properties">{quickStats.properties_listed}+</p>
                <p className="text-white/60">Properties Listed</p>
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="stats-clients">{quickStats.happy_clients}+</p>
                <p className="text-white/60">Happy Clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Estate Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50" data-testid="realestate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Building2 className="w-4 h-4" /> Real Estate
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Your Property Journey Starts Here
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Whether you&apos;re buying, selling, or a developer looking to showcase projects, we have solutions tailored for you.
            </p>
          </div>
          
          {/* Process Trail */}
          <div className="mb-16 px-4">
            <ProcessTrail steps={realEstateProcess} activeStep={activeRealEstateStep} />
          </div>
          
          {/* User Types Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            <UserTypeCard 
              icon={Search}
              title="Property Seekers"
              description="Find your dream home or investment property with our verified listings."
              features={[
                'Smart search with 50+ filters',
                'Virtual tours & HD photos',
                'Price comparison tools',
                'Expert property advice'
              ]}
              ctaText="Search Properties"
              ctaLink="/real-estate"
              color="#15803D"
            />
            <UserTypeCard 
              icon={HomeIcon}
              title="Property Sellers"
              description="List your property with confidence and reach genuine buyers."
              features={[
                'Free property valuation',
                'Professional photography',
                'Targeted marketing',
                'Negotiation support'
              ]}
              ctaText="List Your Property"
              ctaLink="/real-estate"
              color="#0EA5E9"
            />
            <UserTypeCard 
              icon={Briefcase}
              title="Developers & Builders"
              description="Showcase your projects to a premium audience with our developer partnerships."
              features={[
                'Premium project listings',
                'Lead generation',
                'Marketing campaigns',
                'Dedicated account manager'
              ]}
              ctaText="Partner With Us"
              ctaLink="/real-estate"
              color="#D97706"
            />
          </div>
        </div>
      </section>

      {/* Interior Design Section */}
      <section className="py-20 md:py-32 bg-[#FDFBF7]" data-testid="interior-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Palette className="w-4 h-4" /> Interior Design
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Transform Your Space with Z-Engine
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Our proprietary Z-Engine algorithm provides instant, accurate cost estimates based on {selectedCity} market rates. Get transparent pricing with no hidden costs.
              </p>
              
              {/* Features List */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: Sparkles, text: 'Instant AI-powered quotes in seconds' },
                  { icon: Users, text: 'Expert designers with 10+ years experience' },
                  { icon: Shield, text: 'Quality guaranteed with milestone payments' },
                  { icon: Clock, text: 'On-time delivery commitment' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                size="lg"
                onClick={() => {
                  const cityParam = selectedCity ? `?city=${encodeURIComponent(selectedCity)}` : '';
                  navigate(`/interior-design${cityParam}`);
                }}
                className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-8"
              >
                Get Instant Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80"
                  alt="Modern Interior Design"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Process Trail Overlay */}
              <div className="absolute -bottom-8 left-4 right-4 bg-white rounded-xl shadow-xl p-6">
                <p className="text-sm font-semibold text-gray-500 mb-4">Your Design Journey</p>
                <ProcessTrail steps={interiorProcess} activeStep={activeInteriorStep} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Property Management Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-gray-900 to-gray-800 text-white" data-testid="management-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80"
                  alt="Property Management"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Stats Card */}
              <div className="absolute -bottom-6 -right-6 bg-white text-gray-900 rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center">
                    <Key className="w-7 h-7 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">98%</p>
                    <p className="text-gray-500 text-sm">Occupancy Rate</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-violet-500/20 text-violet-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Key className="w-4 h-4" /> Property Management
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Let Us Handle Your Property
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                From finding tenants to maintenance and rent collection, we take care of everything so you can enjoy passive income without the hassle.
              </p>
              
              {/* Process Trail */}
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 mb-8">
                <p className="text-sm font-semibold text-gray-400 mb-4">How It Works</p>
                <ProcessTrail steps={managementProcess} activeStep={activeManagementStep} />
              </div>
              
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  onClick={() => {
                    const cityParam = selectedCity ? `?city=${encodeURIComponent(selectedCity)}` : '';
                    navigate(`/property-management${cityParam}`);
                  }}
                  className="rounded-full bg-violet-500 hover:bg-violet-600 text-white px-8"
                >
                  List Your Property <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    const cityParam = selectedCity ? `?city=${encodeURIComponent(selectedCity)}` : '';
                    navigate(`/rentals/find${cityParam}`);
                  }}
                  className="rounded-full border-white/30 text-white hover:bg-white/10 px-8"
                >
                  Find Rentals
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Join thousands of satisfied clients who trust ZenStudios for their property and design needs.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="rounded-full bg-white text-primary hover:bg-white/90 px-12 py-6 text-lg shadow-xl"
              data-testid="cta-register-btn"
            >
              Get Started Free
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20would%20like%20to%20know%20more%20about%20your%20services.', '_blank')}
              className="rounded-full border-white/50 text-white hover:bg-white/10 px-12 py-6 text-lg"
              data-testid="contact-us-btn"
            >
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* CSS for progress animation */}
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress 5s linear;
        }
      `}</style>
    </div>
  );
};

export default Home;
